import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Loader } from "../../../../Shared/Common/Loader";
import NoRecord from "../../../../Shared/Common/NoRecord";
import { ArrowDown, ArrowUp } from "../../../../Shared/Icons";
import { StringRecord } from "../../../../Shared/Type";
import { SortingTypeI } from "../../../../Utils/consts";
import CreateExtractionService from "../../../../Services/InfectiousDisease/CreateExtractionService";
import Row from "./Row";
import useIsMobile from "Shared/hooks/useIsMobile";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";

import useLang from "Shared/hooks/useLanguage";

export interface IRows {
  rackDetailId: number;
  rackName: string;
  rackID: string;
  createdDate: string;
  createdBy: string;
  rackStatus: string;
}

interface SearchRequest {
  rackName: string;
  rackID: string;
  createdDate: string;
  createdBy: string;
  rackStatus: string;
}

// Constants
const INITIAL_SORTING_TYPE = {
  clickedIconData: "rackDetailId",
  sortingOrder: "desc",
} as const;

const INITIAL_SEARCH_QUERY = {
  rackName: "",
  rackID: "",
  createdDate: "",
  createdBy: "",
  rackStatus: "",
} as const;

const QUERY_DISPLAY_TAG_NAMES: StringRecord = {
  rackName: "Rack Name",
  rackID: "Rack ID",
  createdDate: "Created Date",
  createdBy: "Created By",
  rackStatus: "Rack Status",
} as const;

const DEFAULT_PAGE_SIZE = 50;

export default function ExtractionRacksScanningLog() {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [triggerSearchData, setTriggerSearchData] = useState(false);
  const [searchRequest, setSearchRequest] =
    useState<SearchRequest>(INITIAL_SEARCH_QUERY);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);

  // Sorting state
  const [sort, setSorting] = useState<SortingTypeI>(INITIAL_SORTING_TYPE);

  // Pagination states
  const [curPage, setCurPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);

  const nextPage = () => {
    if (curPage < Math.ceil(total / pageSize)) {
      setCurPage(curPage + 1);
    }
  };
  const showPage = (i: number) => {
    setCurPage(i);
  };
  const prevPage = () => {
    if (curPage > 1) {
      setCurPage(curPage - 1);
    }
  };
  useEffect(() => {
    setTotalPages(Math.ceil(total / pageSize));
    const pgNumbers = [];
    for (let i = curPage - 2; i <= curPage + 2; i++) {
      if (i > 0 && i <= totalPages) {
        pgNumbers.push(i);
      }
    }
    setPageNumbers(pgNumbers);
  }, [total, curPage, pageSize, totalPages]);

  // Data states
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<IRows[]>([]);

  // API call tracking to prevent concurrent calls
  const isApiCallInProgress = useRef(false);

  // Simple cache to avoid duplicate requests
  const lastRequestParams = useRef<string>("");

  // Debounce timer ref
  ///////const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize data on component mount
  useEffect(() => {
    loadGridData(true, false);
  }, []);

  // Consolidated effect for data loading - debounced to prevent multiple rapid calls
  useEffect(() => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced API call
    debounceTimer.current = setTimeout(() => {
      loadGridData(true, false);
    }, 300); // 300ms debounce

    // Cleanup timer on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [curPage, pageSize, triggerSearchData, sort]);

  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };
  const resetSearch = () => {
    setSearchRequest(INITIAL_SEARCH_QUERY);
    setSorting(INITIAL_SORTING_TYPE);
    // Force refresh with reset parameters
    lastRequestParams.current = "";
    loadGridData(true, true, INITIAL_SORTING_TYPE);
  };

  const loadGridData = (
    loader: boolean,
    reset: boolean,
    sortingState?: any
  ) => {
    // Prevent concurrent API calls
    if (isApiCallInProgress.current) {
      return;
    }

    const nullObj = {
      rackName: "",
      rackID: "",
      createdDate: "",
      createdBy: "",
      rackStatus: "",
    };

    const trimmedSearchRequest = Object.fromEntries(
      Object.entries(searchRequest).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    // Ensure queryModel has the correct structure with ReqTypeId
    const queryModel = reset
      ? nullObj
      : {
          rackName: trimmedSearchRequest.rackName || "",
          rackID: trimmedSearchRequest.rackID || "",
          createdDate: trimmedSearchRequest.createdDate || "",
          createdBy: trimmedSearchRequest.createdBy || "",
          rackStatus: trimmedSearchRequest.rackStatus || "",
        };

    const requestObj = {
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel: queryModel,
      sortColumn: reset ? sortingState?.clickedIconData : sort?.clickedIconData,
      sortDirection: reset ? sortingState?.sortingOrder : sort?.sortingOrder,
    };

    // Create cache key to avoid duplicate requests
    const cacheKey = JSON.stringify(requestObj);

    // Skip API call if same parameters as last request
    if (lastRequestParams.current === cacheKey && !reset) {
      return;
    }

    lastRequestParams.current = cacheKey;
    isApiCallInProgress.current = true;

    if (loader) {
      setLoading(true);
    }

    CreateExtractionService.GetExtractionRacksScanningLogs(requestObj)
      .then((res: AxiosResponse) => {
        setRows(res?.data?.data);
        setTotal(res?.data?.total);
      })
      .catch((err: any) => {
        console.trace(err, "err");
      })
      .finally(() => {
        setLoading(false);
        isApiCallInProgress.current = false;
      });
  };

  const searchRef = useRef<any>(null);

  const handleSort = (columnName: any) => {
    const currentOrder = searchRef.current.id;
    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    searchRef.current.id = newOrder;

    setSorting({
      sortingOrder: newOrder,
      clickedIconData: columnName,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Clear debounce timer and trigger immediate search
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      loadGridData(true, false);
    }
  };

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prev) => ({
      ...prev,
      [clickedTag]: (INITIAL_SEARCH_QUERY as any)[clickedTag],
    }));
  };

  useEffect(() => {
    const activeTags = Object.entries(searchRequest)
      .filter(([, value]) => value)
      .map(([key]) => key);
    setSearchedTags(activeTags);
  }, [searchRequest]);

  // Removed automatic resetSearch to prevent unintended search parameter resets
  // The resetSearch function should only be called explicitly by user action

  return (
    <>
      <div className="app-content flex-column-fluid p-0">
        <div className="card-body py-2">
          <div className="d-flex gap-4 flex-wrap mb-1">
            {searchedTags.map((tag) =>
              tag === "isArchived" ? null : (
                <div
                  key={tag}
                  className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                  onClick={() => handleTagRemoval(tag)}
                >
                  <span className="fw-bold">
                    {t(QUERY_DISPLAY_TAG_NAMES[tag])}
                  </span>
                  <i className="bi bi-x"></i>
                </div>
              )
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
            <div className="d-flex gap-2 responsive-flexed-actions">
              <div className="d-flex align-items-center">
                <span className="fw-400 mr-3">{t("Records")}</span>
                <select
                  id={`IDExtractionRacksScanningLogRecords`}
                  className="form-select w-125px h-33px rounded py-2"
                  data-kt-select2="true"
                  data-placeholder="Select option"
                  data-dropdown-parent="#kt_menu_63b2e70320b73"
                  data-allow-clear="true"
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="50" selected>
                    50
                  </option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                id={`IDExtractionRacksScanningLogSearch`}
                onClick={() => {
                  setCurPage(1);
                  setTriggerSearchData((prev) => !prev);
                }}
                className="btn btn-linkedin btn-sm fw-500 py-2 rounded-3"
                aria-controls="Search"
              >
                {t("Search")}
              </button>
              <button
                onClick={resetSearch}
                type="button"
                className="btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
                id={`IDExtractionRacksScanningLogReset`}
              >
                <span>
                  <span>{t("Reset")}</span>
                </span>
              </button>
            </div>
          </div>

          <div className="card">
            <Box sx={{ height: "auto", width: "100%" }}>
              <div className="table_bordered overflow-hidden">
                <TableContainer
                  sx={
                    isMobile
                      ? {
                          overflowY: "hidden",
                        }
                      : {}
                  }
                  className="shadow-none"
                >
                  <Table
                    // stickyHeader
                    aria-label="sticky table collapsible"
                    className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                  >
                    <TableHead>
                      <TableRow className="h-40px">
                        <TableCell></TableCell>
                        <TableCell className="min-w-50px"></TableCell>
                        <TableCell>
                          <input
                            id={`IDExtractionRacksScanningLogRackName`}
                            type="text"
                            name="rackName"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search...")}
                            value={searchRequest.rackName}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            id={`IDExtractionRacksScanningLogRackID`}
                            type="text"
                            name="rackID"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search...")}
                            value={searchRequest.rackID}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                        <TableCell>
                          <SearchDatePicker
                            name="createdDate"
                            value={searchRequest.createdDate}
                            onChange={onInputChangeSearch}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            id={`IDExtractionRacksScanningLogCreatedBy`}
                            type="text"
                            name="createdBy"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search...")}
                            value={searchRequest.createdBy}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            id={`IDExtractionRacksScanningLogRackStatus`}
                            type="text"
                            name="rackStatus"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search...")}
                            value={searchRequest.rackStatus}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow className="h-30px">
                        <TableCell className="w-20px min-w-20px"></TableCell>
                        <TableCell className="w-50px min-w-50px text-center">
                          {t("Actions")}
                        </TableCell>

                        <TableCell
                          className="min-w-250px w-250px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("rackName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Rack Name")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "rackName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "rackName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>

                        <TableCell
                          className="min-w-200px w-200px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("rackID")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Rack ID")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "rackID"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "rackID"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>

                        <TableCell
                          className="min-w-200px w-200px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("createdDate")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Date of Creation")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "createdDate"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "createdDate"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>

                        <TableCell
                          className="min-w-200px w-200px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("createdBy")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Created By")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "createdBy"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "createdBy"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>

                        <TableCell
                          className="min-w-150px w-150px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("rackStatus")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Rack Status")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "rackStatus"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "rackStatus"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableCell colSpan={9} className="padding-0">
                          <Loader />
                        </TableCell>
                      ) : !rows.length ? (
                        <NoRecord message={"No data found for this table"} />
                      ) : (
                        rows.map((item: any, index: any) => {
                          return (
                            <Row
                              key={item.rackDetailId || index}
                              row={item}
                              index={index}
                              loadGridData={loadGridData}
                            />
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Box>
          </div>

          <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
            <p className="pagination-total-record mb-0">
              {Math.min(pageSize * curPage, total) === 0 ? (
                <span>
                  {t("Showing 0 to 0 of")} {total} {t("entries")}
                </span>
              ) : (
                <span>
                  {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
                  {Math.min(pageSize * curPage, total)} {t("of Total")}{" "}
                  <span> {total} </span> {t("entries")}{" "}
                </span>
              )}
            </p>
            <ul className="d-flex align-items-center justify-content-end custome-pagination mb-0 p-0">
              <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
                <i className="fa fa-angle-double-left"></i>
              </li>
              <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
                <i className="fa fa-angle-left"></i>
              </li>

              {pageNumbers.map((page) => (
                <li
                  key={page}
                  className={`px-2 ${
                    page === curPage
                      ? "font-weight-bold bg-primary text-white h-33px"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => showPage(page)}
                >
                  {page}
                </li>
              ))}

              <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
                <i className="fa fa-angle-right"></i>
              </li>
              <li
                className="btn btn-lg p-2 h-33px"
                onClick={() => {
                  if (totalPages === 0) {
                    showPage(curPage);
                  } else {
                    showPage(totalPages);
                  }
                }}
              >
                <i className="fa fa-angle-double-right"></i>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
